import { useEffect, useState } from 'react';
import {
  BadgeCheck,
  Building2,
  CheckCircle2,
  Fingerprint,
  IdCard,
  LockKeyhole,
  ShieldCheck,
  UserRound,
  XCircle,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { api, dateOnly, dateTime } from '../lib/api';

const titleCase = value => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase());

function credentialLabel(row) {
  if (row?.credential_label) return row.credential_label;
  return row?.relationship_source === 'companies_house'
    ? 'Corporate Officer Identification'
    : 'Staff Identification';
}

export default function CompanyIDVerify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api(`/api/company-id/verify/${encodeURIComponent(token || '')}`)
      .then(result => { if (!cancelled) setData(result); })
      .catch(caught => { if (!cancelled) setError(caught.message || 'This Company ID could not be verified.'); });
    return () => { cancelled = true; };
  }, [token]);

  if (error) return <VerificationShell>
    <section className="cidv-result cidv-invalid cidv-formal-result">
      <div className="cidv-result-icon"><XCircle size={34}/></div>
      <span className="cidv-kicker">LIVE COMPANY CREDENTIAL CHECK</span>
      <h1 className="govuk-heading-xl">Company ID not verified</h1>
      <p className="govuk-body">{error}</p>
      <div className="cidv-help">Do not rely on a screenshot or saved copy. Ask the holder to reopen their Company ID and present a fresh QR code.</div>
    </section>
  </VerificationShell>;

  if (!data) return <VerificationShell>
    <section className="cidv-result cidv-formal-result">
      <div className="cidv-loader"/>
      <span className="cidv-kicker">LIVE COMPANY CREDENTIAL CHECK</span>
      <h1 className="govuk-heading-xl">Checking Company ID</h1>
      <p className="govuk-body">Confirming the issuer, credential status, current staff relationship and company authority…</p>
    </section>
  </VerificationShell>;

  const row = data.credential || {};
  const verification = data.verification || {};
  const valid = Boolean(data.valid);
  const role = row.internal_title || titleCase(row.companies_house_role) || 'Staff member';

  return <VerificationShell>
    <section className={`cidv-result cidv-formal-result ${valid ? 'cidv-valid' : 'cidv-invalid'}`}>
      <div className="cidv-result-icon">{valid ? <BadgeCheck size={36}/> : <XCircle size={36}/>}</div>
      <span className="cidv-kicker">{valid ? 'LIVE VERIFICATION PASSED' : 'LIVE VERIFICATION FAILED'}</span>
      <h1 className="govuk-heading-xl">{valid ? 'Valid company-issued credential' : 'Credential not currently valid'}</h1>
      <p className="govuk-body">{valid
        ? `The issuer confirms that this credential is current for ${row.legal_name || 'the named holder'} at the time shown below.`
        : 'Do not rely on this credential as current company identification or evidence of present company authority.'}</p>
      <div className={`cidv-live ${valid ? '' : 'failed'}`}><span/> Checked live · {dateTime(verification.checked_at)}</div>
      {verification.verification_reference && <div className="cidv-check-ref">Verification reference <strong>{verification.verification_reference}</strong></div>}
    </section>

    <section className={`cidv-formal-card ${valid ? 'valid' : 'invalid'}`}>
      <header className="cidv-formal-card-head">
        <div className="cidv-formal-brand">
          <div className="cidv-formal-mark"><Fingerprint size={20}/></div>
          <div><strong>{row.issuer_name || row.company_name || 'SOUSA MURRAY'}</strong><span>COMPANY IDENTIFICATION CREDENTIAL</span></div>
        </div>
        <div className={`cidv-formal-status ${valid ? 'valid' : 'invalid'}`}>{valid ? <BadgeCheck size={17}/> : <XCircle size={17}/>} {valid ? 'VALID' : titleCase(row.status)}</div>
      </header>

      <div className="cidv-formal-holder">
        <div className="cidv-formal-photo">
          {row.photo_url
            ? <img src={row.photo_url} alt="Credential holder"/>
            : <div><UserRound size={34}/><span>PHOTO<br/>NOT ON FILE</span></div>}
        </div>
        <div className="cidv-formal-name">
          <span>{credentialLabel(row)}</span>
          <h2 className="govuk-heading-m">{row.legal_name || 'Unknown holder'}</h2>
          <p className="govuk-body">{role}</p>
          <div className="cidv-formal-staff-id"><span>STAFF ID</span><strong>{row.staff_id_number || '—'}</strong></div>
        </div>
      </div>

      <div className="cidv-formal-company">
        <Building2 size={17}/>
        <div><span>ISSUING LEGAL ENTITY</span><strong>{row.company_name || 'Unknown company'}</strong></div>
        <span>Company No. {row.company_number || '—'}</span>
      </div>

      <div className="cidv-formal-facts">
        <Fact label="Credential serial" value={row.credential_number}/>
        <Fact label="Credential standard" value={row.credential_format || 'SMCID-1'}/>
        <Fact label="Credential type" value={credentialLabel(row)}/>
        <Fact label="Credential status" value={titleCase(row.status)} good={row.status === 'active'}/>
        <Fact label="Authority source" value={row.relationship_source === 'employer' ? 'Employer staff record' : 'Companies House'}/>
        <Fact label="Company role" value={role}/>
        <Fact label="Relationship" value={titleCase(row.relationship_status)} good={['verified','employer_verified'].includes(row.relationship_status)}/>
        <Fact label="Staff relationship" value={verification.employment_current ? 'Current' : 'Not current'} good={Boolean(verification.employment_current)}/>
        <Fact label="Company status" value={verification.company_active ? 'Active' : titleCase(row.company_status)} good={Boolean(verification.company_active)}/>
        <Fact label="Holder photograph" value={verification.holder_photo_present ? 'On file' : 'Not on file'} good={Boolean(verification.holder_photo_present)}/>
        <Fact label="Identity assurance" value={verification.identity_assurance === 'company_checked' ? 'Company checked' : 'Legacy — not recorded'} good={verification.identity_assurance === 'company_checked'}/>
        <Fact label="Issued" value={dateOnly(row.issued_at)}/>
        <Fact label="Valid until" value={row.expires_at ? dateOnly(row.expires_at) : 'Until revoked'}/>
      </div>

      <footer className="cidv-formal-foot">
        <span>Employer-issued company credential · Not government identification</span>
        <strong>{row.credential_format || 'SMCID-1'}</strong>
      </footer>
    </section>

    <section className="cidv-assurance cidv-formal-assurance">
      <div className="cidv-assurance-title"><ShieldCheck size={20}/><div><strong>Verification checks</strong><span>Live assurance from the issuing Company ID platform</span></div></div>
      <div className="cidv-assurance-grid">
        <Assurance ok={row.status === 'active'} title="Credential status" body="Checked against the issuer's current credential record."/>
        <Assurance ok={Boolean(verification.employment_current)} title="Staff relationship" body="The holder remains linked to a current staff record."/>
        <Assurance ok={Boolean(verification.relationship_current)} title="Company authority" body="The claimed company relationship is current."/>
        <Assurance ok={Boolean(verification.company_active)} title="Legal entity status" body="The linked company is currently active."/>
        <Assurance ok={Boolean(verification.holder_photo_present)} neutral={!verification.holder_photo_present} title="Holder photograph" body={verification.holder_photo_present ? 'A holder photograph is present on the issuer record.' : 'No holder photograph is stored on this credential.'}/>
        <Assurance ok={verification.identity_assurance === 'company_checked'} neutral={verification.identity_assurance !== 'company_checked'} title="Identity assurance" body={verification.identity_assurance === 'company_checked' ? 'The issuer recorded a holder identity check when this credential was issued.' : 'No formal identity-assurance record exists for this legacy credential.'}/>
        <div><LockKeyhole size={17}/><span><strong>Private by design</strong><small>Companies House personal codes and restricted internal identifiers are not disclosed.</small></span></div>
      </div>
    </section>

    <section className="cidv-scope">
      <IdCard size={20}/>
      <div>
        <strong>What this credential proves</strong>
        <p className="govuk-body">This verification confirms the issuing company's current credential record and the relationship shown above. It is company-issued identification only; it is not a passport, driving licence or other government identity document.</p>
      </div>
    </section>
  </VerificationShell>;
}

function Fact({ label, value, good }) {
  return <div className="cidv-fact"><span>{label}</span><strong className={good ? 'good' : ''}>{value || '—'}</strong></div>;
}

function Assurance({ ok, neutral, title, body }) {
  return <div className={neutral ? 'neutral' : ok ? 'ok' : 'bad'}>
    {neutral ? <IdCard size={17}/> : ok ? <CheckCircle2 size={17}/> : <XCircle size={17}/>}
    <span><strong>{title}</strong><small>{body}</small></span>
  </div>;
}

function VerificationShell({ children }) {
  return <main className="cidv-page">
    <header className="cidv-header cidv-formal-header">
      <div className="cidv-logo"><Fingerprint size={22}/><div><strong>SOUSA MURRAY</strong><span>COMPANY ID VERIFICATION</span></div></div>
      <span className="cidv-secure"><ShieldCheck size={14}/> Live issuer check</span>
    </header>
    <div className="cidv-content">{children}</div>
    <footer className="cidv-page-footer">Sousa Murray Company ID · Secure credential verification</footer>
  </main>;
}
